/**
 * B2 · CHAT DEL AULA — mutaciones local-first.
 *
 * Escriben en PowerSync y suben por el write-path (`db/sync.rs`), que es quien
 * MANDA: deriva `organization_id` de la cohorte, exige matrícula ACTIVA, aplica
 * el rate limit, la ventana de edición y el catálogo cerrado de emojis, y solo
 * acepta ocultar/fijar/promover a quien modera. Lo de aquí es la vista
 * optimista, nunca la autorización.
 *
 * Los ids de `cohort_chat_read` y `cohort_chat_reaction` son DETERMINISTAS y
 * los genera el cliente. No es un capricho: ambas tablas tienen una `UNIQUE`
 * natural, así que con ids aleatorios dos dispositivos de la misma persona
 * escribirían dos filas y la segunda se perdería contra el índice. El servidor
 * comprueba el formato y descarta lo que no cuadre.
 */

import {
  and,
  cohort_chat_message,
  cohort_chat_reaction,
  cohort_chat_read,
  eq,
  type PowerSyncDrizzleDB,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateUUID } from "../../utils/uuid";

/**
 * Emojis admitidos. Espejo EXACTO de `CHAT_ALLOWED_EMOJI`
 * (`core-rust/src/use_cases/classroom.rs`) y del CHECK de la migración 00092:
 * el servidor descarta cualquier otro, así que ofrecer más aquí solo produciría
 * reacciones que desaparecen al sincronizar.
 */
export const CHAT_REACTION_EMOJIS = ["👍", "🎉", "❤️", "😮", "🤔", "✅"] as const;
export type ChatReactionEmoji = (typeof CHAT_REACTION_EMOJIS)[number];

/** Tope de caracteres (== `cohort_chat_body_len`, migración 00091). */
export const CHAT_MAX_BODY_CHARS = 4000;

/** Minutos durante los que el autor puede corregir su propio mensaje. */
export const CHAT_EDIT_WINDOW_MINUTES = 5;

/** Id determinista de MI marca de leído en un aula. */
export function chatReadId(cohortId: string, userId: string): string {
  return `chatread:${cohortId}:${userId}`;
}

/** Id determinista de MI reacción a un mensaje. */
export function chatReactionId(messageId: string, userId: string): string {
  return `chatreact:${messageId}:${userId}`;
}

/** Envía un mensaje al chat de una cohorte. Devuelve su id. */
export async function sendChatMessageMutation(
  db: PowerSyncDrizzleDB,
  params: { cohortId: string; organizationId?: string | null; authorId: string; body: string },
): Promise<string> {
  const now = nowISO();
  const id = generateUUID();
  await db.insert(cohort_chat_message).values({
    id,
    cohort_id: params.cohortId,
    // Optimista: el servidor lo REESCRIBE con el de la cohorte. Se rellena solo
    // para que la fila local sea coherente hasta el primer checkpoint.
    organization_id: params.organizationId ?? null,
    author_id: params.authorId,
    body: params.body.trim().slice(0, CHAT_MAX_BODY_CHARS),
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Corrige el propio mensaje (el servidor exige autoría y ventana). */
export async function editChatMessageMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
  body: string,
): Promise<void> {
  await db
    .update(cohort_chat_message)
    .set({ body: body.trim().slice(0, CHAT_MAX_BODY_CHARS), updated_at: nowISO() })
    .where(eq(cohort_chat_message.id, messageId));
}

/** Borra el propio mensaje (o cualquiera, si quien lo pide modera). */
export async function deleteChatMessageMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(cohort_chat_message)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(cohort_chat_message.id, messageId));
}

/**
 * Oculta o restaura un mensaje. Solo el servidor decide si quien lo pide
 * modera; ocultar SACA el mensaje de la réplica del alumno, no lo esconde.
 */
export async function setChatMessageHiddenMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
  hidden: boolean,
): Promise<void> {
  const now = nowISO();
  await db
    .update(cohort_chat_message)
    .set({ hidden_at: hidden ? now : null, updated_at: now })
    .where(eq(cohort_chat_message.id, messageId));
}

/** Fija o suelta un mensaje (queda exento de la ventana de retención). */
export async function setChatMessagePinnedMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
  pinned: boolean,
): Promise<void> {
  const now = nowISO();
  await db
    .update(cohort_chat_message)
    .set({ pinned_at: pinned ? now : null, updated_at: now })
    .where(eq(cohort_chat_message.id, messageId));
}

/**
 * Enlaza un mensaje con el hilo de foro al que se promovió. El servidor
 * comprueba que el hilo sea de ESTA cohorte: sin eso, el chip y su enlace
 * apuntarían a un foro ajeno.
 */
export async function linkChatMessageToThreadMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
  threadId: string,
): Promise<void> {
  await db
    .update(cohort_chat_message)
    .set({ promoted_thread_id: threadId, updated_at: nowISO() })
    .where(eq(cohort_chat_message.id, messageId));
}

/**
 * Pone al día MI marca de leído del aula. Idempotente por el id determinista;
 * el servidor además solo deja que la marca AVANCE (una operación encolada
 * offline no resucita no-leídos ya leídos en otro dispositivo).
 */
export async function markChatReadMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    organizationId?: string | null;
    userId: string;
    at?: string;
    mutedUntil?: string | null;
  },
): Promise<void> {
  const now = nowISO();
  const id = chatReadId(params.cohortId, params.userId);
  const existing = await db
    .select({ id: cohort_chat_read.id })
    .from(cohort_chat_read)
    .where(eq(cohort_chat_read.id, id))
    .limit(1);
  const values = {
    last_read_at: params.at ?? now,
    ...(params.mutedUntil === undefined ? {} : { muted_until: params.mutedUntil }),
    updated_at: now,
  };
  if (existing.length > 0) {
    await db.update(cohort_chat_read).set(values).where(eq(cohort_chat_read.id, id));
    return;
  }
  await db.insert(cohort_chat_read).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId ?? null,
    user_id: params.userId,
    created_at: now,
    ...values,
  });
}

/** Silencia (o vuelve a activar) los avisos de ESTA aula. */
export async function setChatMutedMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    organizationId?: string | null;
    userId: string;
    mutedUntil: string | null;
  },
): Promise<void> {
  await markChatReadMutation(db, { ...params, at: undefined });
}

/** Añade o cambia MI reacción a un mensaje (una por mensaje y persona). */
export async function reactToChatMessageMutation(
  db: PowerSyncDrizzleDB,
  params: {
    messageId: string;
    cohortId: string;
    organizationId?: string | null;
    userId: string;
    emoji: ChatReactionEmoji;
  },
): Promise<void> {
  const id = chatReactionId(params.messageId, params.userId);
  const existing = await db
    .select({ id: cohort_chat_reaction.id })
    .from(cohort_chat_reaction)
    .where(eq(cohort_chat_reaction.id, id))
    .limit(1);
  if (existing.length > 0) {
    await db
      .update(cohort_chat_reaction)
      .set({ emoji: params.emoji })
      .where(eq(cohort_chat_reaction.id, id));
    return;
  }
  await db.insert(cohort_chat_reaction).values({
    id,
    message_id: params.messageId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId ?? null,
    user_id: params.userId,
    emoji: params.emoji,
    created_at: nowISO(),
  });
}

/** Retira MI reacción (borrado real: una reacción retirada no deja rastro). */
export async function removeChatReactionMutation(
  db: PowerSyncDrizzleDB,
  messageId: string,
  userId: string,
): Promise<void> {
  await db
    .delete(cohort_chat_reaction)
    .where(
      and(
        eq(cohort_chat_reaction.id, chatReactionId(messageId, userId)),
        eq(cohort_chat_reaction.user_id, userId),
      ),
    );
}
